import { useState, useContext, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import { COLORS } from "../theme/tokens";
import FlatCard from "../components/FlatCard";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import { useServers } from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { useTableSelection } from "../hooks/useTableSelection";
import SelectionActionBar from "../components/SelectionActionBar";
import AddServerModal from "../components/AddServerModal";

// Spec palette (blue · light). Servers have no status colour, so they use the
// app's primary blue identity. Sourced from the shared design tokens.
const BLUE = COLORS.primary;
const BLUE_SOFT = COLORS.primarySoft;

// --- CSV import / export (spec §4.3) -----------------------------------------
const SERVER_CSV_COLUMNS = [
  "id", "name", "ownership", "public_ip", "private_ip", "hosting_provider",
  "panel_url", "panel_username", "expiry_date",
];

function serverToCsvRow(s) {
  return {
    id: s.id ?? "",
    name: s.name || "",
    ownership: s.ownership || "",
    public_ip: s.publicIp || "",
    private_ip: s.privateIp || "",
    hosting_provider: s.hostingProvider || "",
    panel_url: s.panelUrl || "",
    panel_username: s.panelUsername || "",
    expiry_date: s.expiry || "",
  };
}

// CSV snake_case header → the camelCase payload key /servers/import expects.
const CSV_TO_PAYLOAD = {
  id: "id",
  name: "name",
  ownership: "ownership",
  public_ip: "publicIp",
  private_ip: "privateIp",
  hosting_provider: "hostingProvider",
  panel_url: "panelUrl",
  panel_username: "panelUsername",
  expiry_date: "expiry",
};

function escapeCsvField(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function rowsToCsv(columns, rows) {
  const header = columns.join(",");
  const lines = rows.map((row) =>
    columns.map((c) => escapeCsvField(row[c])).join(",")
  );
  return [header, ...lines].join("\n");
}

function downloadCsv(filename, csv) {
  // UTF-8 BOM so Excel opens accented characters correctly (spec §5.6).
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function parseCsv(text) {
  const rows = [];
  let current = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      current.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      current.push(field);
      rows.push(current);
      current = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || current.length) {
    current.push(field);
    rows.push(current);
  }
  return rows.filter((r) => r.some((c) => c !== ""));
}

function ServerList() {
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    menuBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
  };
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("servers", "add");
  const canEdit = hasPermission("servers", "edit");
  const canDelete = hasPermission("servers", "delete");

  const [search, setSearch] = useState("");

  // Add / Edit Server modal (spec §5.3). `editServerId` null = the Add modal;
  // an id = the Edit modal for that row.
  const [addOpen, setAddOpen] = useState(false);
  const [editServerId, setEditServerId] = useState(null);
  const modalOpen = addOpen || editServerId !== null;

  // View modal
  const [viewServer, setViewServer] = useState(null);

  function closeServerModal() {
    setAddOpen(false);
    setEditServerId(null);
  }

  function handleServerSaved({ isEdit } = {}) {
    closeServerModal();
    toast.success(isEdit ? "Server updated successfully" : "Server added successfully");
  }

  // --- Import / Export CSV (spec §4.3) ---
  const [ioAnchor, setIoAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  async function handleExportCsv() {
    setIoAnchor(null);
    try {
      const all = await api.get("/servers");
      if (!all.length) {
        toast.info("No servers to export yet");
        return;
      }
      const dateStr = new Date().toISOString().slice(0, 10);
      downloadCsv(
        `servers-${dateStr}.csv`,
        rowsToCsv(SERVER_CSV_COLUMNS, all.map(serverToCsvRow))
      );
      toast.success(`Exported ${all.length} servers`);
    } catch (err) {
      toast.error(err.message || "Failed to export servers");
    }
  }

  function handleImportClick() {
    setIoAnchor(null);
    setImportFile(null);
    setImportOpen(true);
  }

  function handleDownloadTemplate() {
    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCsv(`servers-template-${dateStr}.csv`, SERVER_CSV_COLUMNS.join(","));
  }

  function handleFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setImportFile(file);
  }

  function closeImportDialog() {
    if (importing) return;
    setImportOpen(false);
    setImportFile(null);
  }

  async function handleImportConfirm() {
    if (!importFile) return;
    setImporting(true);
    let text;
    try {
      text = await importFile.text();
    } catch {
      setImporting(false);
      toast.error("Could not read the file");
      return;
    }
    const rows = parseCsv(text);
    if (rows.length < 2) {
      setImporting(false);
      toast.error("CSV has no data rows");
      return;
    }
    // Strip a leading UTF-8 BOM from the first header, map snake_case → payload.
    const headers = rows[0].map((h, i) =>
      i === 0 ? String(h).replace(/^﻿/, "") : h
    );
    const servers = rows.slice(1).map((values) => {
      const obj = {};
      headers.forEach((h, i) => {
        const key = CSV_TO_PAYLOAD[h] || h;
        if (key === "id") return; // never trust an imported id
        obj[key] = values[i] ?? "";
      });
      return obj;
    });
    try {
      const result = await api.post("/servers/import", { servers });
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      const added = result?.added || 0;
      const updated = result?.updated || 0;
      const failed = result?.failed || 0;
      toast.success(
        `Import complete: ${added} added, ${updated} updated${
          failed ? `, ${failed} failed` : ""
        }`
      );
      setImportOpen(false);
      setImportFile(null);
    } catch (err) {
      toast.error(`Import failed: ${err?.message || "server error"}`);
    } finally {
      setImporting(false);
    }
  }

  // Server inventory comes from the shared, cached React Query hook. The list
  // endpoint returns a bare array; normalise defensively in case a paginated
  // { rows } shape comes back.
  const { data: serversData } = useServers();
  const servers = Array.isArray(serversData)
    ? serversData
    : Array.isArray(serversData?.rows)
    ? serversData.rows
    : [];

  function handleEdit(server) {
    setEditServerId(server.id);
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/servers/${id}`);
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      toast.info("Server deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete server");
    }
  }

  const filtered = servers.filter((s) =>
    (s.name || "").toLowerCase().includes(search.toLowerCase())
  );

  // --- Row selection + bulk delete ---
  const pageIds = filtered.map((s) => s.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} server${ids.length === 1 ? "" : "s"}?`,
      message: "The selected servers will be permanently removed.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/servers/bulk-delete", { ids });
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} server(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected servers");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleDeleteAll() {
    const ok = await confirm({
      title: "Delete ALL servers?",
      message:
        "Every server in the table will be permanently removed. This cannot be undone.",
      danger: true,
      confirmLabel: "Delete all",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/servers/delete-all");
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? 0} server(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete all servers");
    } finally {
      setBulkBusy(false);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
    "& textarea": { color: colors.text },
  };

  // Single source of truth for the table grid so the header and every row
  // share the exact same column tracks (guarantees they stay aligned).
  // Leading 44px track is the checkbox column.
  const GRID_COLUMNS =
    "44px minmax(200px, 2fr) 110px 150px minmax(160px, 1.3fr) 120px 120px 130px";

  return (
    <DashboardLayout>
      {/* Header row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Servers
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {servers.length} {servers.length === 1 ? "server" : "servers"} in inventory
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ alignSelf: { xs: "flex-end", sm: "center" }, ml: { sm: "auto" }, justifyContent: { xs: "flex-end" } }}
        >
          {canDelete && selectedIds.size > 0 && (
            <Button
              onClick={handleDeleteAll}
              startIcon={<DeleteOutlineIcon />}
              disabled={bulkBusy}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2,
                py: 1.1,
                borderRadius: "10px",
                color: "#DC2626",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                transition: "all 0.15s ease",
              }}
            >
              Delete All
            </Button>
          )}

          <Button
            onClick={(e) => setIoAnchor(e.currentTarget)}
            startIcon={<ImportExportIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2,
              py: 1.1,
              borderRadius: "10px",
              color: colors.text,
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              "&:hover": {
                background: colors.hover,
                borderColor: colors.cardBorderHover,
              },
              transition: "all 0.15s ease",
            }}
          >
            Import / Export
          </Button>

          {canAdd && (
            <Button
              onClick={() => setAddOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                borderRadius: "10px",
                color: "#fff",
                background:
                  "#3B82F6",
                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
                "&:hover": {
                  boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.15s ease",
              }}
            >
              Add Server
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Search */}
      <FlatCard sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search server by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 20 }}
              />
            ),
          }}
          sx={fieldSx}
        />
      </FlatCard>

      {/* Bulk-delete selection bar */}
      <SelectionActionBar
        count={selectedIds.size}
        onDelete={canDelete ? handleDeleteSelected : undefined}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* List */}
      {filtered.length === 0 ? (
        <FlatCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3B82F6",
              background: "rgba(59, 130, 246, 0.12)",
              border: "1px solid rgba(59, 130, 246, 0.25)",
            }}
          >
            <DnsOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            {servers.length === 0
              ? "No servers yet"
              : "No matches found"}
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: colors.textMuted }}
          >
            {servers.length === 0
              ? "Click \"Add Server\" to register your first server"
              : "Try a different search term"}
          </Typography>
        </FlatCard>
      ) : (
        <FlatCard sx={{ overflow: "hidden" }}>
          {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
          <Box sx={{ overflowX: "auto" }}>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              minWidth: { xs: "max-content", md: "auto" },
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 1.75,
              borderBottom: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              fontSize: 12,
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                sx={{ p: 0, color: colors.textMuted }}
              />
            </Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Server Name</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Ownership</Box>
            <Box sx={{ fontFamily: HEADING_FONT, pl: 2 }}>IP Address</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Hosting Provider</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Panel</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Expiry</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Actions</Box>
          </Box>

          {/* Rows */}
          {filtered.map((server) => (
            <Box
              key={server.id}
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                minWidth: { xs: "max-content", md: "auto" },
                gap: 2,
                px: 3,
                py: 2,
                alignItems: "center",
                borderBottom: `1px solid ${colors.divider}`,
                "&:last-child": { borderBottom: "none" },
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  background: BLUE,
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                },
                "&:hover": { background: "#F8FAFC" },
                "&:hover::before": { opacity: 1 },
                transition: "background 0.18s ease",
              }}
            >
              {/* Row select checkbox */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={selectedIds.has(server.id)}
                  onChange={() => toggleRow(server.id)}
                  sx={{ p: 0, color: colors.textMuted }}
                />
              </Box>

              {/* Server Name */}
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    background: BLUE_SOFT,
                    color: BLUE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <DnsOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 14,
                      fontWeight: 600,
                      color: colors.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {server.name || "(unnamed)"}
                  </Typography>
                  {server.panelUsername && (
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 12,
                        color: colors.textMuted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {server.panelUsername}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* Ownership */}
              <Box>
                <Chip
                  label={server.ownership === "self" ? "Self" : "Client"}
                  size="small"
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                    fontSize: 11,
                    color:
                      server.ownership === "self"
                        ? "#16A34A"
                        : "#D97706",
                    background:
                      server.ownership === "self"
                        ? "rgba(16, 185, 129, 0.12)"
                        : "rgba(245, 158, 11, 0.12)",
                    border: `1px solid ${
                      server.ownership === "self"
                        ? "rgba(16, 185, 129, 0.3)"
                        : "rgba(245, 158, 11, 0.3)"
                    }`,
                  }}
                />
              </Box>

              {/* IP */}
              <Typography
                sx={{
                  fontSize: 13,
                  color: colors.text,
                  pl: 2,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {server.publicIp || "—"}
              </Typography>

              {/* Hosting Provider */}
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 13,
                  color: colors.textMuted,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {server.hostingProvider || "—"}
              </Typography>

              {/* Panel link */}
              <Box sx={{ textAlign: "center" }}>
                {server.panelUrl ? (
                  <Tooltip title={server.panelUrl}>
                    <Button
                      href={server.panelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      size="small"
                      sx={{
                        fontFamily: HEADING_FONT,
                        textTransform: "none",
                        fontSize: 12,
                        color: "#3B82F6",
                        px: 1,
                        py: 0.25,
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.08)",
                        },
                      }}
                    >
                      Open
                    </Button>
                  </Tooltip>
                ) : (
                  <Typography
                    sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textFaint }}
                  >
                    —
                  </Typography>
                )}
              </Box>

              {/* Expiry */}
              <Typography
                sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}
              >
                {server.expiry || "—"}
              </Typography>

              {/* Actions */}
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="flex-start"
                sx={{ ml: -0.5 }}
              >
                <Tooltip title="View">
                  <IconButton
                    size="small"
                    onClick={() => setViewServer(server)}
                    sx={{
                      width: 32, height: 32, borderRadius: 1.5,
                      color: "#0ea5e9",
                      background: "rgba(14, 165, 233, 0.1)",
                      border: "1px solid rgba(14, 165, 233, 0.25)",
                      "&:hover": { background: "rgba(14, 165, 233, 0.18)" },
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(server)}
                      sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        color: "#3B82F6",
                        background: "rgba(59, 130, 246, 0.1)",
                        border: "1px solid rgba(59, 130, 246, 0.25)",
                        "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: "Delete server?",
                            message: "This action cannot be undone.",
                            danger: true,
                            confirmLabel: "Delete",
                          })
                        )
                          handleDelete(server.id);
                      }}
                      sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        color: "#DC2626",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.25)",
                        "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
          </Box>
        </FlatCard>
      )}

      {/* ── Server View Modal ──────────────────────────────────────────── */}
      {(() => {
        const vs = viewServer;
        if (!vs) return null;
        // Compute expiry status for the badge
        const today = new Date(); today.setHours(0,0,0,0);
        const expiryDate = vs.expiry ? new Date(vs.expiry) : null;
        const daysLeft = expiryDate ? Math.ceil((expiryDate - today) / 86400000) : null;
        const expired = daysLeft !== null && daysLeft < 0;
        const nearExpiry = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
        const statusColor = expired ? "#DC2626" : nearExpiry ? "#D97706" : "#16A34A";
        const statusBg = expired ? "rgba(220,38,38,0.12)" : nearExpiry ? "rgba(217,119,6,0.12)" : "rgba(22,163,74,0.12)";
        const statusLabel = expired ? "Expired" : nearExpiry ? "Near Expiry" : "Active";
        return (
          <Dialog
            open
            onClose={() => setViewServer(null)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                background: "#ffffff",
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: 3,
                overflow: "hidden",
              },
            }}
          >
            {/* Blue gradient banner */}
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1.75,
                px: 3, py: 2.5, color: "#fff",
                background: `linear-gradient(135deg, #1D4ED8 0%, ${BLUE} 100%)`,
              }}
            >
              <Box sx={{ width: 46, height: 46, borderRadius: "12px", background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <DnsOutlinedIcon sx={{ fontSize: 25 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", opacity: 0.85 }}>Server</Typography>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 21, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.15 }}>{vs.name || "(unnamed)"}</Typography>
              </Box>
              {/* Expiry / status badge in banner */}
              <Box
                component="span"
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.6,
                  px: 1.5, py: 0.5, borderRadius: "999px",
                  background: "rgba(255,255,255,0.22)",
                  color: "#fff", fontWeight: 700, fontSize: 11,
                  mr: 1, flexShrink: 0,
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", flexShrink: 0 }} />
                {statusLabel}
              </Box>
              <IconButton onClick={() => setViewServer(null)} sx={{ color: "rgba(255,255,255,0.9)", "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" } }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ px: 3, py: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {[
                { label: "Public IP", value: vs.publicIp || "—", mono: true },
                { label: "Private IP", value: vs.privateIp || "—", mono: true },
                { label: "Ownership", value: vs.ownership === "self" ? "Self" : "Client" },
                { label: "Hosting Provider", value: vs.hostingProvider || "—" },
                { label: "Expiry Date", value: vs.expiry || "—" },
                { label: "Panel Username", value: vs.panelUsername || "—", mono: true },
                { label: "Panel URL", value: vs.panelUrl || "—", link: vs.panelUrl },
              ].map(({ label, value, mono, link }) => (
                <Box key={label} sx={{ p: 1.75, borderRadius: "10px", border: `1px solid ${colors.cardBorder}`, background: colors.hover }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: colors.textMuted, mb: 0.5 }}>{label}</Typography>
                  {link ? (
                    <Box component="a" href={link} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, fontWeight: 600, color: BLUE, textDecoration: "none", "&:hover": { textDecoration: "underline" }, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Box>
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.text, fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined }}>{value}</Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Status badges row */}
            <Box sx={{ px: 3, pb: 2.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: statusColor, background: statusBg }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
                {statusLabel}
              </Box>
              {daysLeft !== null && (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: colors.textMuted, background: colors.tile }}>
                  {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
                </Box>
              )}
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: vs.ownership === "self" ? "#16A34A" : "#D97706", background: vs.ownership === "self" ? "rgba(22,163,74,0.12)" : "rgba(217,119,6,0.12)" }}>
                {vs.ownership === "self" ? "Self-owned" : "Client server"}
              </Box>
            </Box>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, borderTop: `1px solid ${colors.cardBorder}` }}>
              <Button onClick={() => setViewServer(null)} sx={{ textTransform: "none", fontWeight: 600, px: 3, color: colors.textMuted, "&:hover": { background: colors.hover } }}>
                Close
              </Button>
              {canEdit && (
                <Button
                  onClick={() => { setViewServer(null); handleEdit(vs); }}
                  sx={{ textTransform: "none", fontWeight: 600, px: 3, color: "#fff", background: BLUE, "&:hover": { boxShadow: "0 8px 22px rgba(59,130,246,0.45)" } }}
                >
                  Edit
                </Button>
              )}
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Import / Export menu (spec §4.3) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={handleFilePicked}
      />
      <Menu
        anchorEl={ioAnchor}
        open={Boolean(ioAnchor)}
        onClose={() => setIoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: "10px",
              background: COLORS.surface,
              border: `1px solid ${colors.cardBorder}`,
            },
          },
        }}
      >
        {canAdd && (
          <MenuItem onClick={handleImportClick} sx={{ color: colors.text }}>
            <ListItemIcon>
              <FileUploadOutlinedIcon sx={{ fontSize: 20, color: colors.textMuted }} />
            </ListItemIcon>
            <ListItemText primary="Import CSV" />
          </MenuItem>
        )}
        <MenuItem onClick={handleExportCsv} sx={{ color: colors.text }}>
          <ListItemIcon>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: colors.textMuted }} />
          </ListItemIcon>
          <ListItemText primary="Export CSV" />
        </MenuItem>
      </Menu>

      {/* Import CSV dialog */}
      <Dialog
        open={importOpen}
        onClose={closeImportDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: COLORS.surface,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: HEADING_FONT,
            color: colors.text,
            fontWeight: 700,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          Import Servers
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 2 }}>
            Upload a CSV with columns:{" "}
            <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
              {SERVER_CSV_COLUMNS.join(", ")}
            </Box>
            . Rows are matched by name — existing servers are updated, new ones added.
          </Typography>
          <Button
            onClick={handleDownloadTemplate}
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              mb: 2,
              color: BLUE,
              "&:hover": { background: BLUE_SOFT },
            }}
          >
            Download CSV template
          </Button>
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `1.5px dashed ${importFile ? BLUE : colors.cardBorder}`,
              borderRadius: "12px",
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              background: importFile ? BLUE_SOFT : colors.hover,
              transition: "all 0.15s ease",
              "&:hover": { borderColor: BLUE },
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 30, color: BLUE, mb: 0.5 }} />
            <Typography sx={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
              {importFile ? importFile.name : "Click to choose a .csv file"}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: 12, mt: 0.5 }}>
              {importFile ? "Click to choose a different file" : ".csv only"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}` }}>
          <Button
            onClick={closeImportDialog}
            disabled={importing}
            sx={{
              textTransform: "none",
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImportConfirm}
            disabled={!importFile || importing}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background: BLUE,
              "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.6)",
                background: colors.cardBorder,
              },
            }}
          >
            {importing ? "Importing…" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Server modal (spec §5.3) */}
      <AddServerModal
        open={modalOpen}
        editId={editServerId}
        onClose={closeServerModal}
        onSuccess={handleServerSaved}
      />
    </DashboardLayout>
  );
}

export default ServerList;
